import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import { categories } from '../../mocks/categories';
import { useSEO } from '../../hooks/useSEO';
import PricingCards from './components/PricingCards';
import SubmitFAQ from './components/SubmitFAQ';

export default function SubmitTool() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';

  useSEO({
    title: 'Submit Your AI Tool — Get Listed on ToolNova.ai',
    description: 'Submit your AI tool to ToolNova.ai and get discovered by thousands of AI enthusiasts. Free and paid listing options available.',
    keywords: 'submit AI tool, AI tool directory, featured listing, AI tool submission',
    canonical: `${siteUrl}/submit-tool`,
    ogTitle: 'Submit Your AI Tool — Get Listed on ToolNova.ai',
    ogDescription: 'Submit your AI tool to ToolNova.ai and get discovered by thousands of AI enthusiasts. Free and paid listing options available.',
    ogType: 'website',
    ogUrl: `${siteUrl}/submit-tool`,
    ogImage: 'https://public.readdy.ai/ai/img_res/2a35743cdb9600edc45f9b5e09018b91.jpg',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Submit Your AI Tool to ToolNova',
        description: 'Submit your AI tool to ToolNova for a free or featured listing. Get discovered by creators, marketers, and entrepreneurs actively searching for AI solutions.',
        url: `${siteUrl}/submit-tool`,
        publisher: {
          '@type': 'Organization',
          name: 'ToolNova',
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/81ddcca4e455aa4e3e9404d7fe6eefde/e7fd262c64409f252189cfe0fbf7a092.png',
          },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Submit an AI Tool to ToolNova',
        description: 'Submit your AI tool to our directory in 3 simple steps.',
        url: `${siteUrl}/submit-tool`,
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: 'Choose a Listing Type',
            text: 'Select a Free, Featured, or Sponsored listing based on your visibility goals.',
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: 'Fill Out the Submission Form',
            text: 'Provide your tool name, website URL, category, description, logo, and contact email.',
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: 'Await Editorial Review',
            text: 'Our team reviews every submission before publishing to ensure quality.',
          },
        ],
      },
    ],
  });

  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    toolName: '',
    websiteUrl: '',
    category: '',
    description: '',
    logo: null as File | null,
    contactEmail: ''
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formDataToSend = new FormData(form);
    try {
      await fetch('https://readdy.ai/api/form/d6p3mqp30mftb86r3u4g', {
        method: 'POST',
        body: formDataToSend
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, logo: file }));
  };

  const scrollToForm = () => {
    const el = document.getElementById('submit-tool-form-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar />

      <main className="pb-20">

        {!submitted ? (
          <>
            {/* ── Pricing Cards — top of page, above the fold ── */}
            <PricingCards onSubmitFreeClick={scrollToForm} />

            {/* ── Hero ── */}
            <section className="relative pt-4 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
              {/* Background glow */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-teal-500/5 rounded-full blur-3xl"></div>
              </div>

              <div className="relative max-w-4xl mx-auto text-center">
                <button
                  onClick={scrollToForm}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-teal-500 hover:bg-teal-400 text-white text-base font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-teal-500/30 hover:shadow-teal-400/40 hover:scale-[1.03] active:scale-[0.98]"
                >
                  <i className="ri-send-plane-line text-lg"></i>
                  Submit Your Tool Now — It&apos;s Free
                </button>
              </div>
            </section>

            {/* ── FAQ ── */}
            <SubmitFAQ />

            {/* ── Divider ── */}
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
              <div className="h-px bg-gradient-to-r from-transparent via-teal-500/30 to-transparent"></div>
            </div>

            {/* ── Submission Form ── */}
            <section id="submit-tool-form-section" className="px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-500/10 border border-teal-500/20 rounded-full mb-5">
                    <i className="ri-add-circle-line text-teal-400 text-sm"></i>
                    <span className="text-xs font-semibold text-teal-400 uppercase tracking-wider">Free Submission</span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">
                    Submit Your AI Tool
                  </h2>
                  <p className="text-base text-gray-400 leading-relaxed max-w-xl mx-auto mb-4">
                    Fill out the form below to submit your tool for a free listing. We review every submission before publishing.
                  </p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/5 border border-teal-500/20 rounded-lg">
                    <i className="ri-shield-check-line text-teal-400 text-sm"></i>
                    <span className="text-xs text-gray-300">All submissions are reviewed before being published.</span>
                  </div>
                </div>

                <form
                  id="submit-tool-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-gray-900/40 border border-gray-800/60 rounded-2xl p-8 sm:p-10 backdrop-blur-sm"
                >
                  <div className="space-y-6">

                    {/* Tool Name */}
                    <div>
                      <label htmlFor="toolName" className="block text-sm font-semibold text-gray-300 mb-2">
                        Tool Name <span className="text-teal-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="toolName"
                        name="toolName"
                        value={formData.toolName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800/80 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-200 text-sm"
                        placeholder="e.g., ChatGPT, Midjourney, Notion AI"
                      />
                    </div>

                    {/* Website URL & Category */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="websiteUrl" className="block text-sm font-semibold text-gray-300 mb-2">
                          Website URL <span className="text-teal-400">*</span>
                        </label>
                        <input
                          type="url"
                          id="websiteUrl"
                          name="websiteUrl"
                          value={formData.websiteUrl}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800/80 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-200 text-sm"
                          placeholder="https://yourtool.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="category" className="block text-sm font-semibold text-gray-300 mb-2">
                          Category <span className="text-teal-400">*</span>
                        </label>
                        <select
                          id="category"
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800/80 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-200 cursor-pointer text-sm"
                        >
                          <option value="" className="bg-gray-900">Select a category</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.name} className="bg-gray-900">
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Short Description */}
                    <div>
                      <label htmlFor="description" className="block text-sm font-semibold text-gray-300 mb-2">
                        Short Description <span className="text-teal-400">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                        maxLength={200}
                        rows={4}
                        className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800/80 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-200 resize-none text-sm"
                        placeholder="Briefly describe what your tool does and its key features (max 200 characters)"
                      />
                      <p className="text-xs text-gray-500 mt-2 text-right">
                        {formData.description.length}/200 characters
                      </p>
                    </div>

                    {/* Logo Upload & Contact Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="logo" className="block text-sm font-semibold text-gray-300 mb-2">
                          Logo Upload <span className="text-teal-400">*</span>
                        </label>
                        <input
                          type="file"
                          id="logo"
                          name="logo"
                          onChange={handleFileChange}
                          accept="image/*"
                          required
                          className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800/80 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-500/20 file:text-teal-400 hover:file:bg-teal-500/30 file:cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-200 cursor-pointer text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-2">PNG, JPG, or SVG (max 5MB)</p>
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                          Contact Email <span className="text-teal-400">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.contactEmail}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-gray-900/60 border border-gray-800/80 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition-all duration-200 text-sm"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                      <button
                        type="submit"
                        className="w-full px-6 py-4 bg-teal-500 hover:bg-teal-400 text-white text-base font-semibold rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-teal-500/25 hover:shadow-teal-400/40 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                      >
                        <i className="ri-send-plane-line text-lg"></i>
                        Submit Tool for Review
                      </button>
                    </div>

                  </div>
                </form>
              </div>
            </section>
          </>
        ) : (
          /* Success Message */
          <div className="flex items-center justify-center min-h-[70vh] px-4">
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-teal-500/20 border-2 border-teal-500/40 rounded-full mb-8 animate-pulse">
                <i className="ri-check-line text-4xl text-teal-400"></i>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Thank You for Your Submission!
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed max-w-xl mx-auto mb-8">
                We&apos;ll review your tool and get back to you within a few days. If it&apos;s a great fit for the ToolNova directory, we&apos;ll add it and notify you.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-teal-500/25 hover:shadow-teal-400/40 hover:scale-105 active:scale-95"
              >
                <i className="ri-home-line text-base"></i>
                Back to Home
              </a>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
